import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Timestamp,
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import {
  Check, ChevronRight, ClipboardList, Clock3, FileCheck2, Filter, MessageSquarePlus,
  Phone, Plus, Search, UserRound, UsersRound, X
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { useAuth } from '../../core/auth/AuthProvider';
import { auth, db, parentCreatorAuth } from '../../core/firebase/firebase';
import { createUserWithEmailAndPassword, deleteUser, signOut as signOutSecondary } from 'firebase/auth';
import { PERMISSIONS } from '../../core/permissions/roles';
import FamilyRegistrationForm from './components/FamilyRegistrationForm';
import { classNameFromId, resolveLegacyClassId, useSchoolClasses } from '../../core/school/classes';

const STATUS_OPTIONS = [
  ['all', 'Toutes'], ['nouvelle', 'Nouvelles'], ['called', 'Appelées'],
  ['visit_scheduled', 'Visite programmée'], ['inscrite', 'Acceptées'], ['rejetee', 'Rejetées']
];
const STATUS_LABELS = { nouvelle:'Nouvelle', new:'Nouvelle', to_contact:'À contacter', called:'Appelée', contacted:'Contactée', visit_scheduled:'Visite programmée', accepted:'Acceptée', inscrite:'Acceptée', student_created:'Convertie', rejected:'Rejetée', rejetee:'Rejetée', cancelled:'Annulée' };

const ts = (value) => typeof value?.toMillis === 'function' ? value.toMillis() : typeof value?.seconds === 'number' ? value.seconds * 1000 : typeof value === 'string' ? Date.parse(value) || 0 : 0;
const formatDate = (value) => ts(value) ? new Intl.DateTimeFormat('fr-FR',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}).format(new Date(ts(value))) : 'Date non définie';
const normalize = (value='') => String(value).trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'');
const normalizePhone = (value='') => String(value).replace(/\D/g,'');

function guardianOf(item){
  return item.guardian || {
    fullName: item.parentName || item.parentFullname || item.parentNom || 'Parent non renseigné',
    phone: item.phone || '', email: item.email || '', cin: item.parentCin || '',
    profession: item.parentProfession || '', relationship: item.relationship || '', address: item.address || ''
  };
}
function childrenOf(item){
  if (Array.isArray(item.children) && item.children.length) return item.children;
  return [{ temporaryId:'legacy', fullName:item.studentName || item.studentFullname || item.fullname || 'Élève non renseigné', requestedClassId:item.classId || '', requestedLevel:item.level || item.classLevel || '', birthDate:item.birthDate || '', previousSchool:item.previousSchool || '', difficulties:item.difficulties || '' }];
}
function statusOf(item){ return item.status || 'nouvelle'; }
function matchesStatus(item, selected){
  if(selected==='all') return true;
  const status=statusOf(item);
  if(selected==='nouvelle') return ['nouvelle','new','to_contact'].includes(status);
  if(selected==='called') return ['called','contacted'].includes(status);
  if(selected==='inscrite') return ['inscrite','accepted','student_created'].includes(status);
  if(selected==='rejetee') return ['rejetee','rejected','cancelled'].includes(status);
  return status===selected;
}


async function findExistingParent(guardian) {
  const matches = new Map();
  const phone = normalizePhone(guardian.phone);
  const cin = String(guardian.cin || '').trim();
  const email = String(guardian.email || '').trim();

  async function collect(field, value, matchedBy) {
    if (!value) return;
    const snapshot = await getDocs(query(collection(db, 'parents'), where(field, '==', value)));
    snapshot.forEach((row) => {
      const current = matches.get(row.id) || { ref: row.ref, data: row.data(), matchedBy: [] };
      current.matchedBy.push(matchedBy);
      matches.set(row.id, current);
    });
  }

  // CIN is the strongest identifier. Phone and email are useful fallbacks for
  // public requests, where CIN is intentionally not collected yet.
  await collect('cin', cin, 'CIN');
  await collect('normalizedPhone', phone, 'téléphone');
  await collect('email', email, 'email');

  if (matches.size > 1) {
    throw new Error('Plusieurs dossiers parents correspondent à ces coordonnées. Vérifiez le CIN, le téléphone ou l’email avant de convertir la demande.');
  }

  return matches.size === 1 ? [...matches.values()][0] : null;
}

async function loadExistingChildren(parentRef, parentData) {
  const byId = new Map();
  const ids = Array.isArray(parentData?.childrenIds) ? parentData.childrenIds.filter(Boolean) : [];

  const linkedDocs = await Promise.all(ids.map(async (studentId) => {
    const snapshot = await getDoc(doc(db, 'students', studentId));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
  }));
  linkedDocs.filter(Boolean).forEach((child) => byId.set(child.id, child));

  // Compatibility safeguard for older parent records whose childrenIds array
  // was incomplete but whose student documents already point to the parent.
  const linkedQuery = await getDocs(query(collection(db, 'students'), where('primaryParentId', '==', parentRef.id)));
  linkedQuery.forEach((row) => byId.set(row.id, { id: row.id, ...row.data() }));

  return [...byId.values()];
}

export default function RegistrationsPage(){
  const { classes } = useSchoolClasses();
  const [searchParams] = useSearchParams();
  const { user, profile, can } = useAuth();
  const canManage = can(PERMISSIONS.REGISTRATIONS_MANAGE);
  const [rows,setRows] = useState([]);
  const [selectedId,setSelectedId] = useState(null);
  const [filter,setFilter] = useState(() => searchParams.get('statut') || 'all');
  const [search,setSearch] = useState('');
  const [showCreate,setShowCreate] = useState(false);
  const [note,setNote] = useState('');
  const [username,setUsername] = useState('');
  const [code,setCode] = useState('');
  const [loading,setLoading] = useState(true);
  const [saving,setSaving] = useState(false);
  const [message,setMessage] = useState('');
  const [error,setError] = useState('');

  useEffect(() => onSnapshot(collection(db,'demandes_inscription'), snapshot => {
    setRows(snapshot.docs.map(d=>({id:d.id,...d.data()})).sort((a,b)=>ts(b.createdAt)-ts(a.createdAt)));
    setLoading(false);
  }, err => { console.error(err); setError('Impossible de charger les demandes.'); setLoading(false); }),[]);

  const selected = rows.find(row=>row.id===selectedId) || null;
  const visible = useMemo(() => rows.filter(row => {
    if(!matchesStatus(row,filter)) return false;
    const needle=normalize(search); if(!needle) return true;
    return [guardianOf(row).fullName, guardianOf(row).phone, guardianOf(row).email, ...childrenOf(row).flatMap(c=>[c.fullName,c.requestedLevel])].some(v=>normalize(v).includes(needle));
  }),[rows,filter,search]);
  const counts = useMemo(()=>Object.fromEntries(STATUS_OPTIONS.map(([value])=>[value,rows.filter(r=>matchesStatus(r,value)).length])),[rows]);

  async function createFamily(payload){
    setSaving(true); setError('');
    try{
      await addDoc(collection(db,'demandes_inscription'),{
        schemaVersion:2, applicationType:'family', guardian:payload.guardian, children:payload.children,
        message:payload.message, source:'internal', status:'nouvelle', notes:[], createdAt:serverTimestamp(),
        createdByUid:user?.uid||null, createdByName:profile?.fullname||'Administration'
      });
      setShowCreate(false); setMessage('Demande familiale créée.');
    }catch(err){ console.error(err); setError(`Création impossible : ${err.message}`); }
    finally{ setSaving(false); }
  }

  async function patchSelected(payload, success){
    if(!selected || !canManage) return;
    setSaving(true); setError('');
    try{
      await updateDoc(doc(db,'demandes_inscription',selected.id),{...payload,updatedAt:serverTimestamp(),updatedByUid:user?.uid||null,updatedByName:profile?.fullname||'Administration'});
      setMessage(success);
    }catch(err){ console.error(err); setError(`Action impossible : ${err.message}`); }
    finally{ setSaving(false); }
  }

  async function addNote(event){
    event.preventDefault(); if(!note.trim()) return;
    await patchSelected({notes:arrayUnion({text:note.trim(),adminUid:user?.uid||null,adminName:profile?.fullname||'Administration',createdAt:Timestamp.now()})},'Note ajoutée.');
    setNote('');
  }

  async function convertFamily(){
    if(!selected || !canManage || selected.convertedAt) return;
    setSaving(true); setError('');
    try{
      const guardian=guardianOf(selected);
      const children=childrenOf(selected);
      const phoneKey=normalizePhone(guardian.phone);
      const existingParent=await findExistingParent(guardian);
      const parentRef=existingParent?.ref || doc(collection(db,'parents'));

      if(existingParent){
        const matchedBy=existingParent.matchedBy.join(', ');
        const existingName=existingParent.data.fullName||existingParent.data.fullname||'ce parent';
        const confirmed=window.confirm(`Famille existante trouvée (${matchedBy}) : ${existingName}.

Voulez-vous rattacher les enfants de cette demande à ce dossier parent existant ?`);
        if(!confirmed){
          setMessage('Conversion annulée : aucun dossier n’a été modifié.');
          return;
        }
      }

      const existingChildren=existingParent ? await loadExistingChildren(parentRef, existingParent.data) : [];
      const existingByName=new Map(existingChildren.map((child)=>[normalize(child.fullName||child.fullname||''),child]));
      const batch=writeBatch(db);
      const resolvedStudents=[];
      const newStudentRefs=[];

      children.forEach((child)=>{
        const duplicate=existingByName.get(normalize(child.fullName));
        if(duplicate){
          resolvedStudents.push({id:duplicate.id, existing:true});
          return;
        }
        const studentRef=doc(collection(db,'students'));
        newStudentRefs.push(studentRef);
        resolvedStudents.push({id:studentRef.id, ref:studentRef, child, existing:false});
      });

      if(existingParent){
        if(newStudentRefs.length){
          batch.update(parentRef,{
            childrenIds:arrayUnion(...newStudentRefs.map((ref)=>ref.id)),
            updatedAt:serverTimestamp()
          });
        }
      }else{
        batch.set(parentRef,{
          fullName:guardian.fullName, fullname:guardian.fullName, phone:guardian.phone, normalizedPhone:phoneKey,
          email:guardian.email||'', cin:guardian.cin||'', profession:guardian.profession||'', relationship:guardian.relationship||'',
          address:guardian.address||'', childrenIds:newStudentRefs.map((ref)=>ref.id), status:'invited', activationStatus:'not_prepared',
          registrationId:selected.id, updatedAt:serverTimestamp(), createdAt:serverTimestamp(), createdByUid:user?.uid||null
        });
      }

      resolvedStudents.filter((entry)=>!entry.existing).forEach((entry)=>{
        const classId = entry.child.requestedClassId || resolveLegacyClassId(entry.child.requestedLevel, classes);
        const className = classNameFromId(classId, classes, entry.child.requestedLevel || '');
        batch.set(entry.ref,{
          fullName:entry.child.fullName, fullname:entry.child.fullName, birthDate:entry.child.birthDate||'',
          classId, className, classLabel:className, level:className,
          requestedClassId:classId, requestedLevel:className,
          previousSchool:entry.child.previousSchool||'', difficulties:entry.child.difficulties||'',
          parentIds:[parentRef.id], primaryParentId:parentRef.id, parentName:guardian.fullName, parentPhone:guardian.phone,
          parentEmail:guardian.email||'', registrationId:selected.id, status:'active', createdAt:serverTimestamp(), createdByUid:user?.uid||null
        });
      });

      const registrationUpdate={
        status:'student_created', parentId:parentRef.id, studentIds:resolvedStudents.map((entry)=>entry.id), studentId:resolvedStudents[0]?.id||null,
        linkedToExistingParent:Boolean(existingParent), parentMatchedBy:existingParent?.matchedBy||[],
        convertedAt:serverTimestamp(), convertedByUid:user?.uid||null, convertedByName:profile?.fullname||'Administration', updatedAt:serverTimestamp()
      };

      if(existingParent?.data?.userUid){
        registrationUpdate.parentUserUid=existingParent.data.userUid;
        registrationUpdate.parentUsername=existingParent.data.username||'';
        registrationUpdate.activationStatus='existing_account';
      }

      batch.update(doc(db,'demandes_inscription',selected.id),registrationUpdate);
      await batch.commit();

      const duplicates=resolvedStudents.filter((entry)=>entry.existing).length;
      const created=resolvedStudents.length-duplicates;
      if(existingParent){
        setMessage(`${created} nouvel${created>1?'s':''} élève${created>1?'s':''} rattaché${created>1?'s':''} à la famille existante${duplicates?` · ${duplicates} élève${duplicates>1?'s':''} déjà lié${duplicates>1?'s':''}`:''}.`);
      }else{
        setMessage(`${created} élève${created>1?'s':''} et le dossier parent ont été créés.`);
      }
    }catch(err){ console.error(err); setError(`Conversion impossible : ${err.message}`); }
    finally{ setSaving(false); }
  }

  function suggestUsername(){
    const base=normalize(guardianOf(selected).fullName).replace(/[^a-z0-9]+/g,'.').replace(/^\.|\.$/g,'');
    setUsername(base || `parent.${Date.now().toString().slice(-4)}`);
  }
  function generateCode(){ const alphabet='ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#'; let value=''; crypto.getRandomValues(new Uint32Array(12)).forEach(n=>{value+=alphabet[n%alphabet.length]}); setCode(value); }
  async function createActivationTask(event){
    event.preventDefault();
    if(!selected || selected.parentUserUid || !username.trim() || !code.trim() || !selected.parentId) return;

    const cleanUsername=normalize(username).replace(/[^a-z0-9._-]/g,'');
    const temporaryPassword=code.trim();
    if(!cleanUsername){ setError('Nom d’utilisateur invalide.'); return; }
    if(temporaryPassword.length<8){ setError('Le mot de passe temporaire doit contenir au moins 8 caractères.'); return; }

    setSaving(true); setError('');
    let createdUser=null;
    try{
      const parentRow=selected.parentId;
      const parentName=guardianOf(selected).fullName;
      const parentPhone=guardianOf(selected).phone||'';
      const authEmail=`${cleanUsername}@parents.le-defi.local`;

      const credential=await createUserWithEmailAndPassword(parentCreatorAuth,authEmail,temporaryPassword);
      createdUser=credential.user;

      const taskRef=doc(collection(db,'tasks'));
      const sms=`École Le Défi\n\nVotre compte parent est prêt.\n\nNom d’utilisateur : ${cleanUsername}\nMot de passe temporaire : ${temporaryPassword}\n\nConnectez-vous sur le-defi.com.`;
      const batch=writeBatch(db);

      batch.set(doc(db,'users',createdUser.uid),{
        uid:createdUser.uid,
        accountType:'parent', role:'parent', status:'approved',
        parentId:parentRow, username:cleanUsername, email:authEmail,
        mustChangePassword:true, onboardingCompleted:false, contractAccepted:false,
        createdAt:serverTimestamp(), createdByUid:auth.currentUser?.uid||user?.uid||null
      });
      batch.update(doc(db,'parents',parentRow),{
        userUid:createdUser.uid, username:cleanUsername, authEmail,
        activationStatus:'pending_sms', activationTaskId:taskRef.id, updatedAt:serverTimestamp()
      });
      batch.update(doc(db,'demandes_inscription',selected.id),{
        parentUserUid:createdUser.uid, parentUsername:cleanUsername,
        activationStatus:'pending_sms', activationTaskId:taskRef.id, updatedAt:serverTimestamp()
      });
      batch.set(taskRef,{
        type:'sms', subtype:'send_parent_activation',
        title:`Envoyer les accès à ${parentName}`,
        description:sms, smsMessage:sms, status:'open', priority:'high',
        sourceType:'registration', sourceId:selected.id, registrationId:selected.id,
        parentId:parentRow, parentName, phone:parentPhone,
        username:cleanUsername, temporaryPassword,
        assignedToUid:user?.uid||null, createdByUid:user?.uid||null,
        createdByName:profile?.fullname||'Administration',
        createdAt:serverTimestamp(), updatedAt:serverTimestamp()
      });

      await batch.commit();
      setMessage('Compte parent créé et tâche SMS préparée.');
      setUsername(''); setCode('');
    }catch(err){
      console.error(err);
      if(createdUser){ await deleteUser(createdUser).catch(()=>{}); }
      const duplicate=err?.code==='auth/email-already-in-use';
      setError(duplicate ? 'Ce nom d’utilisateur est déjà utilisé.' : `Activation impossible : ${err.message || 'erreur inconnue'}`);
    }finally{
      await signOutSecondary(parentCreatorAuth).catch(()=>{});
      setSaving(false);
    }
  }

  return <>
    <PageHeader eyebrow="MODULE" title="Inscriptions" description="Gérez les demandes familiales, plusieurs enfants et leur conversion en dossiers définitifs."/>
    {error && <div className="dashboard-alert"><div><strong>Une erreur est survenue.</strong><span>{error}</span></div></div>}
    {message && !selected && <div className="success-note registration-global-message"><Check size={17}/>{message}</div>}

    <section className="registration-toolbar panel">
      <label className="registration-search"><Search size={18}/><input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un parent, enfant, téléphone…"/></label>
      <div className="filter-caption"><Filter size={16}/> Filtres</div>
      {canManage && <button className="button button-primary" onClick={()=>setShowCreate(true)}><Plus size={17}/> Nouvelle inscription</button>}
    </section>
    <section className="status-tabs">{STATUS_OPTIONS.map(([value,label])=><button key={value} className={filter===value?'status-tab active':'status-tab'} onClick={()=>setFilter(value)}><span>{label}</span><strong>{counts[value]||0}</strong></button>)}</section>

    <section className="registration-layout">
      <article className="panel registration-panel">
        <div className="panel-heading"><div><p className="eyebrow">DEMANDES FAMILIALES</p><h2>{visible.length} résultat{visible.length>1?'s':''}</h2></div><span className="live-badge"><span/>Mise à jour automatique</span></div>
        <div className="registration-list">
          {loading && <div className="compact-empty"><Clock3 size={22}/>Chargement…</div>}
          {!loading && !visible.length && <div className="compact-empty"><ClipboardList size={22}/>Aucune demande.</div>}
          {visible.map(item=>{const guardian=guardianOf(item), children=childrenOf(item), status=statusOf(item); return <button className="registration-row family-registration-row" key={item.id} onClick={()=>{setSelectedId(item.id);setMessage('');setError('');setUsername('');setCode('');}}>
            <span className="registration-avatar"><UsersRound size={19}/></span>
            <span className="registration-main"><strong>{guardian.fullName}</strong><small>{children.length} enfant{children.length>1?'s':''} · {children.map(c=>c.fullName).join(', ')}</small><span className="registration-contact">{guardian.phone||'Téléphone non renseigné'} · {formatDate(item.createdAt)}</span></span>
            <span className="registration-side"><span className={`status-chip status-${status}`}>{STATUS_LABELS[status]||status}</span><ChevronRight size={18}/></span>
          </button>})}
        </div>
      </article>
      <aside className="panel registration-summary"><p className="eyebrow">APERÇU</p><h2>Pipeline actuel</h2><div className="pipeline-summary">{STATUS_OPTIONS.slice(1).map(([value,label])=><button key={value} onClick={()=>setFilter(value)}><span>{label}</span><strong>{counts[value]||0}</strong></button>)}</div></aside>
    </section>

    {showCreate && <div className="modal-layer"><div className="family-modal panel"><header><div><p className="eyebrow">INSCRIPTION INTERNE</p><h2>Nouvelle demande familiale</h2></div><button className="drawer-close" onClick={()=>setShowCreate(false)}><X size={20}/></button></header><FamilyRegistrationForm submitting={saving} onSubmit={createFamily} onCancel={()=>setShowCreate(false)}/></div></div>}

    {selected && <div className="drawer-backdrop" onMouseDown={e=>e.target===e.currentTarget&&setSelectedId(null)}><aside className="registration-drawer">
      <header className="drawer-header"><div><p className="eyebrow">DOSSIER FAMILIAL</p><h2>{guardianOf(selected).fullName}</h2><span className={`status-chip status-${statusOf(selected)}`}>{STATUS_LABELS[statusOf(selected)]||statusOf(selected)}</span></div><button className="drawer-close" onClick={()=>setSelectedId(null)}><X size={20}/></button></header>
      <div className="drawer-scroll">
        {message && <div className="success-note"><Check size={17}/>{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}
        <section className="detail-card"><h3>Responsable principal</h3><dl className="detail-grid"><div><dt>Nom</dt><dd>{guardianOf(selected).fullName}</dd></div><div><dt>Téléphone</dt><dd>{guardianOf(selected).phone||'—'}</dd></div><div><dt>Email</dt><dd>{guardianOf(selected).email||'—'}</dd></div><div><dt>CIN</dt><dd>{guardianOf(selected).cin||'—'}</dd></div></dl></section>
        <section className="detail-card"><h3>Enfants concernés</h3><div className="family-children-list">{childrenOf(selected).map((child,index)=><article key={child.temporaryId||index}><span>{index+1}</span><div><strong>{child.fullName}</strong><small>{classNameFromId(child.requestedClassId || resolveLegacyClassId(child.requestedLevel, classes), classes, child.requestedLevel || 'Niveau non renseigné')}{child.birthDate?` · ${child.birthDate}`:''}</small>{child.difficulties&&<p>{child.difficulties}</p>}</div></article>)}</div></section>
        {canManage && <section className="detail-card"><h3>Actions</h3><div className="drawer-actions"><button className="button button-secondary" disabled={saving} onClick={()=>patchSelected({status:'called',calledAt:serverTimestamp()},'Demande marquée comme appelée.')}><Phone size={16}/>Appelée</button><button className="button action-success" disabled={saving} onClick={()=>patchSelected({status:'inscrite',finalStatus:'inscrite',finalizedAt:serverTimestamp()},'Inscription acceptée.')}><FileCheck2 size={16}/>Accepter</button><button className="button action-danger" disabled={saving} onClick={()=>patchSelected({status:'rejetee',finalStatus:'rejetee',finalizedAt:serverTimestamp()},'Demande rejetée.')}><X size={16}/>Rejeter</button></div>{['inscrite','accepted'].includes(statusOf(selected)) && <button className="button convert-button" disabled={saving||Boolean(selected.convertedAt)} onClick={convertFamily}><UsersRound size={17}/>{selected.convertedAt?'Famille déjà convertie':'Créer le parent et les élèves'}</button>}</section>}
        {canManage && selected.parentId && selected.parentUserUid && <section className="detail-card activation-card"><h3><MessageSquarePlus size={18}/>Compte parent</h3><div className="success-note"><Check size={17}/>Famille existante : les nouveaux enfants utilisent le compte parent déjà créé{selected.parentUsername?` (${selected.parentUsername})`:''}.</div></section>}
        {canManage && selected.parentId && !selected.parentUserUid && <section className="detail-card activation-card"><h3><MessageSquarePlus size={18}/>Activation du compte parent</h3>{selected.activationTaskId?<div className="success-note"><Check size={17}/>La tâche SMS existe déjà.</div>:<form className="activation-form" onSubmit={createActivationTask}><label>Nom d’utilisateur<div className="inline-input-action"><input value={username} onChange={e=>setUsername(e.target.value)} required/><button type="button" onClick={suggestUsername}>Proposer</button></div></label><label>Mot de passe temporaire<div className="inline-input-action"><input value={code} onChange={e=>setCode(e.target.value)} required/><button type="button" onClick={generateCode}>Générer</button></div></label><button className="button button-primary" disabled={saving}>Créer la tâche « Envoyer le SMS »</button></form>}</section>}
        <section className="detail-card"><h3>Notes internes</h3><div className="notes-stack">{(selected.notes||[]).map((n,i)=><article className="internal-note" key={i}><header><strong>{n.adminName||'Administration'}</strong><small>{formatDate(n.createdAt)}</small></header><p>{n.text}</p></article>)}</div>{canManage&&<form className="note-form" onSubmit={addNote}><textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="Ajouter une note…"/><button className="button button-primary">Ajouter</button></form>}</section>
      </div>
    </aside></div>}
  </>;
}
